import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils.js";
import type { ButtonHTMLAttributes } from "react";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium",
    "transition-all duration-150 cursor-pointer",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-ring-offset)]",
    "disabled:pointer-events-none disabled:opacity-40",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "bg-[var(--color-accent)] text-[var(--color-accent-foreground)]",
          "hover:bg-[var(--color-accent-hover)] active:scale-[0.98]",
          "shadow-[var(--shadow-xs)]",
        ].join(" "),
        secondary: [
          "bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)]",
          "border border-[var(--color-border)]",
          "hover:bg-[var(--color-muted)] active:scale-[0.98]",
        ].join(" "),
        outline: [
          "border border-[var(--color-border-strong)] bg-transparent text-[var(--color-foreground)]",
          "hover:bg-[var(--color-muted)] active:scale-[0.98]",
        ].join(" "),
        ghost: [
          "text-[var(--color-foreground)] bg-transparent",
          "hover:bg-[var(--color-muted)] active:scale-[0.98]",
        ].join(" "),
        destructive: [
          "bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)]",
          "hover:opacity-85 active:scale-[0.98]",
        ].join(" "),
        link: "text-[var(--color-accent)] underline-offset-4 hover:underline p-0 h-auto shadow-none",
      },
      size: {
        default: "h-9 px-4 py-2 text-sm",
        sm: "h-7 px-3 text-xs rounded",
        lg: "h-11 px-6 text-base",
        xl: "h-12 px-8 text-base",
        icon: "size-9",
        "icon-sm": "size-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

interface Props
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export function Button({
  loading,
  variant,
  size,
  className,
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}
